import { forwardRef, useEffect, useState } from "react";
import DOMPurify from "dompurify";
import { useNavigate } from "react-router-dom";
import { SnackbarContent, CustomContentProps, SnackbarKey, useSnackbar } from "notistack";
import { Badge, Button, Divider, IconButton, Paper, Tooltip, Typography } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";

import { useSignalRContext } from "../Contexts/SignalRContext";
import { NotificationType } from "../../types/Notifications/NotificationBase";
import { notificationTarget } from "../../utilities/helpers/notificationLinks";
import { useAccessibleTarget } from "../AccessLink/useAccessibleTarget";
import { Button as EclipseButton } from "../Buttons/Button";

export default function GlobalNotificationListener() {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { connection } = useSignalRContext();
  const [activeKeys, setActiveKeys] = useState<SnackbarKey[]>([]);

  useEffect(() => {
    if (!connection) return;

    const handleGlobalNotification = (
      rawMessage: string,
      notificationType: string,
      callerUsername: string,
      notificationTimestamp: string
    ) => {
      if (
        notificationType !== "StoppageSetAsAvailableNotification" &&
        notificationType !== "StoppageSetAsUnavailableNotification"
      ) return;

      const safeMessage = DOMPurify.sanitize(rawMessage);

      let notification: NotificationType | null = null;

      try {
        notification = JSON.parse(safeMessage) as NotificationType;
      } catch {
        // not JSON, leave as null
      }

      const key = enqueueSnackbar(safeMessage, {
        persist: true,
        anchorOrigin: { vertical: "top", horizontal: "left" },
        onExited: (_node, exitedKey) =>
          setActiveKeys(prev => prev.filter(k => k !== exitedKey)),
        content: (key, message) => (
          <CustomNotification
            id={key}
            message={notification ? renderNotification(notification, callerUsername) : message}
            notification={notification}
            notificationTimestamp={notificationTimestamp}
            persist={true}
            style={{}}
            anchorOrigin={{ vertical: "top", horizontal: "left" }}
            variant={"default"}
            hideIconVariant={false}
            iconVariant={{}}
          />
        ),
      });

      setActiveKeys(prev => [...prev, key]);
    };

    connection.on("GlobalNotificationAsync", handleGlobalNotification);

    return () => {
      connection.off("GlobalNotificationAsync", handleGlobalNotification);
    };
  }, [connection, enqueueSnackbar, closeSnackbar]);

  const handleDismissAll = () => {
    // Hide the control immediately, then let the toasts animate out. onExited
    // filtering an already-empty list is a harmless no-op.
    const keys = activeKeys;
    setActiveKeys([]);
    keys.forEach(key => closeSnackbar(key));
  };

  const CustomNotification = forwardRef<
    HTMLDivElement,
    CustomContentProps & { notificationTimestamp?: string; notification?: NotificationType | null }
  >((props, ref) => {
    const { id, message, notificationTimestamp, notification, ...other } = props;
    const { closeSnackbar } = useSnackbar();

    return (
      <SnackbarContent ref={ref} {...other}>
        <Paper elevation={6} className="flex flex-col p-4 w-[360px] !bg-theme-surface-2 border border-theme-border">
          <Typography className="text-xl font-bold mb-2 italic text-theme-text" variant="h6">
            Notification
          </Typography>

          <Divider className="my-2 bg-theme-border" />
          <Typography className="text-theme-text mt-2" variant="body1">{message}</Typography>
          <div className="flex justify-between items-center mt-4 gap-2">
            {
              notificationTimestamp && (
                <Typography className="text-theme-text-muted italic text-sm" variant="body2">
                  {notificationTimestamp}
                </Typography>
              )
            }
            <div className="flex items-center gap-2 ml-auto">
              {notification && (
                <StoppageLinkButton notification={notification} onNavigate={() => closeSnackbar(id)} />
              )}
              <Button

                size="small"
                className="!text-white !bg-theme-info hover:!bg-theme-info-hover"
                onClick={() => closeSnackbar(id)}
              >
                Dismiss
              </Button>
            </div>
          </div>
        </Paper>
      </SnackbarContent>
    );
  });

  if (activeKeys.length <= 1) return null;

  // Sits beside the top-left notification stack so it never overlaps the cards.
  return (
    <div className="fixed top-3 left-[372px] z-[1401] animate-fade-in">
      <Tooltip title="Dismiss all notifications" arrow placement="right">
        <Badge badgeContent={activeKeys.length} color="error" overlap="circular">
          <IconButton
            size="small"
            aria-label="Dismiss all notifications"
            onClick={handleDismissAll}
            className="!text-white !bg-theme-info hover:!bg-theme-info-hover !shadow-md"
          >
            <FontAwesomeIcon icon={faXmark} className="w-4 h-4" />
          </IconButton>
        </Badge>
      </Tooltip>
    </div>
  );
}

/** Opens the stoppage the notification is about, for users who can reach the Stoppages page. */
export const StoppageLinkButton: React.FC<{ notification: NotificationType; onNavigate: () => void }> = ({
  notification,
  onNavigate,
}) => {
  const navigate = useNavigate();
  const target = useAccessibleTarget(notificationTarget(notification));

  if (!target) return null;

  return (
    <EclipseButton
      variant="primary"
      size="sm"
      onClick={() => {
        onNavigate();
        navigate(target.to);
      }}
    >
      View stoppage
    </EclipseButton>
  );
};

const renderNotification = (notification: NotificationType, callerUsername?: string) => {
  switch (notification.type) {
    case "StoppageSetAsAvailableNotification":
      return (
        <Typography className="text-theme-text text-lg">
          The status of plant{" "}
          <span className="font-bold" style={{ color: notification.plantColour }}>
            {notification.plantRef}
          </span>{" "}
          has been set as{" "}
          <span className="font-bold text-theme-success">Up</span>
          {callerUsername && (
            <>
              <span> by </span><span className="font-bold">{callerUsername}</span>
            </>
          )}{"."}
        </Typography>
      );

    case "StoppageSetAsUnavailableNotification":
      return (
        <Typography className="text-theme-text text-lg">
          Status of plant{" "}
          <span className='font-bold' style={{ color: notification.plantColour }}>
            {notification.plantRef}
          </span>{" "}
          has been set as{" "}
          <span className="font-bold" style={{ color: notification.statusColour }}>
            {notification.status}
          </span>{" "}
          {callerUsername && (
            <>
              <span> by </span><span className="font-bold">{callerUsername}</span>
            </>
          )}{"."}
        </Typography>
      );

    default:
      return <Typography>You have a new notification.</Typography>;
  }
};